import React, {useState} from 'react';
import {
  Divider,
  Box,
  Typography,
  Grid,
  Tabs,
  Tab,
  IconButton,
  useTheme,
  Dialog,
  Backdrop,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useMediaQuery,
  Avatar,
} from '@mui/material';
import {Fragment} from 'react';
import {faCircleQuestion} from '@fortawesome/free-regular-svg-icons';
import {faTriangleExclamation} from '@fortawesome/pro-light-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {getResultStrenght} from '../../symptomChecker/resultStrenght';
import HorizontalDashIcon from '../svgIcons/resultStrengthDashedIcon';
import {getTotalLinesColourBlue} from '../../utils/miscellaneous';
import {faAngleUp, faAngleDown} from '@fortawesome/free-solid-svg-icons';
import {customScrollbarStyles} from '../../pages/Dashboard/dashboard';

/**
 * React component for displaying buttons and help information.
 *
 * @component
 * @param {Object} props - The component's props.
 * @param {boolean} props.isMostRecent - Indicates whether the information is the most recent.
 * @param {Function} props.handleButtonsInfo - Callback function for handling button interactions.
 * @returns {JSX.Element} React component JSX.
 */
export const ButtonsHelpInfo = ({isMostRecent, handleButtonsInfo}) => {
  return (
    <Grid
      container
      justifyContent={{xs: 'space-between', sm: 'space-between'}}
      alignItems="center"
      columns={13}
      sx={{
        pb: 1,
      }}
    >
      <Grid item xs={12.8} sm={13} md={13} lg={13} sx={{my: 1}}>
        <Button
          variant="contained"
          aria-label="conditionInfo"
          component="label"
          sx={{
            width: '100%',
            backgroundColor: '#3DA9F8',
            color: '#FFFFFF',
            borderRadius: 5,
            fontWeight: 500,
            fontSize: 14,
          }}
          onClick={() => handleButtonsInfo('Condition Information')}
        >
          Condition Information
        </Button>
      </Grid>
      <Grid item xs={6.3} sm={6.3} md={6.3} lg={6.3}>
        <Button
          variant="contained"
          aria-label="treatments"
          component="label"
          sx={{
            width: '100%',
            backgroundColor: '#0CC0DF',
            color: '#FFFFFF',
            borderRadius: 5,
            fontWeight: 500,
            fontSize: 14,
          }}
          onClick={() => handleButtonsInfo('Treatment recommendations')}
        >
          Treatments
        </Button>
      </Grid>
      <Grid item xs={6.4} sm={6.3} md={6.3} lg={6.3}>
        <Button
          variant="contained"
          aria-label="findDoctor"
          component="label"
          sx={{
            width: '100%',
            backgroundColor: '#A6E177',
            color: '#FFFFFF',
            borderRadius: 5,
            fontWeight: 500,
            fontSize: 14,
          }}
          onClick={() => handleButtonsInfo('Find a Doctor')}
        >
          Find a Doctor
        </Button>
      </Grid>
      {!isMostRecent && (
        <Grid item xs={13}>
          <Divider sx={{my: 1.5}} />
        </Grid>
      )}
    </Grid>
  );
};

/**
 * Function representing a tab panel for displaying symptom checker history.
 *
 * @function
 * @param {Object} props - The function's props.
 * @param {Array} props.pastSymptomCheckerHistory - Array of past symptom checker history items.
 * @param {Object} props.mostRecentSymptomChecker - Most recent symptom checker information.
 * @param {Object} props.probColours - Object containing colors for probability indicators.
 * @param {Function} props.handleButtonsInfo - Callback function for handling button interactions.
 * @returns {JSX.Element} React component JSX representing the history tab panel.
 */
function SymptomCheckerHistoryTabPanel({
  pastSymptomCheckerHistory,
  mostRecentSymptomChecker,
  probColours,
  handleButtonsInfo,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [isOpenResultStrength, setIsOpenResultStrenght] = useState(false);
  const [resultsStrength, setResultsStrength] = useState({
    level: '',
    linesColourBlue: 0,
  });
  const [expandedIndices, setExpandedIndices] = useState([]);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const toggleExpanded = index => {
    if (expandedIndices.includes(index)) {
      setExpandedIndices(expandedIndices.filter(i => i !== index));
    } else {
      setExpandedIndices([...expandedIndices, index]);
    }
  };

  // Function to handle click and open the modal for result strenght in symptom checker on dashboard
  const closeModalResultStrenght = isClose => {
    setIsOpenResultStrenght(isClose);
  };

  //Handles action to open result strength modal dialog
  const handleModalResultStrength = condition => {
    const currentResultStrength = getResultStrenght(condition.symptom_conditions[0].probability);
    const currentLinesColourBlue = getTotalLinesColourBlue(currentResultStrength);
    setResultsStrength({
      level: currentResultStrength,
      linesColourBlue: currentLinesColourBlue,
    });
    setIsOpenResultStrenght(true);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const dateObject = new Date(mostRecentSymptomChecker.created_time);
  const splittedDate =
    dateObject.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }) || null;

  const splittedDateMobile =
    dateObject.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: '2-digit',
    }) || null;
  const mostRecentDay = dateObject.toLocaleDateString(undefined, {
    weekday: isSmallScreen ? 'short' : 'long',
  });

  const createdTimeMostRecent = dateObject.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return (
    <div style={{height: '100%'}}>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          ml: 2,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          backgroundColor: 'inherit',
          '& .MuiTabs-indicator': {
            backgroundColor: '#3DA9F8',
          },
        }}
      >
        <Tab
          label="Most Recent"
          sx={{
            fontWeight: 'bold',
            fontSize: '17px',
            height: '100%',
            '&.MuiTab-root': {
              color: 'rgba(61, 169, 248, 0.5)',
            },
            '&.Mui-selected': {
              color: '#3DA9F8',
            },
            '&.MuiTab-textColorInherit.Mui-selected': {
              borderBottom: '2px solid #3DA9F8 !important',
            },
          }}
        />
        <Tab
          label="Past"
          sx={{
            fontWeight: 'bold',
            fontSize: '17px',
            height: '100%',
            '&.MuiTab-root': {
              color: 'rgba(61, 169, 248, 0.5)',
            },
            '&.Mui-selected': {
              color: '#3DA9F8',
            },
            '&.MuiTab-textColorInherit.Mui-selected': {
              borderBottom: '2px solid #3DA9F8 !important',
            },
          }}
        />
      </Tabs>
      {/* Content for Most Recent tab */}
      <TabPanel value={activeTab} index={0}>
        {mostRecentSymptomChecker &&
        mostRecentSymptomChecker.symptom_conditions &&
        mostRecentSymptomChecker.symptom_conditions.length > 0 ? (
          isSmallScreen ? (
            <Fragment>
              <Grid container columns={15} sx={{margin: '0 auto', maxWidth: '800px'}}>
                <Grid item xs={4} sx={{mr: 1}}>
                  <Box sx={{textAlign: 'center'}}>
                    <Typography sx={{fontSize: '13px'}}>{splittedDateMobile}</Typography>
                    <Typography sx={{fontSize: '13px'}}>{mostRecentDay}</Typography>
                    <Typography sx={{fontSize: '13px'}}>{createdTimeMostRecent}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={10} sx={{ml: 1}}>
                  {mostRecentSymptomChecker.symptom_conditions.map((c, subIndex) => (
                    <div
                      key={subIndex}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{flex: 1}}>
                        <Typography sx={{fontWeight: 500, fontSize: '14px'}}>
                          {c.name.replace(/^\s*\w/, match => match.toUpperCase())}
                        </Typography>
                      </div>
                      <div>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '14px',
                            color: subIndex < 1 ? probColours[subIndex].color : probColours[1].color,
                            opacity: subIndex < 1 ? probColours[subIndex].opacity : probColours[1].opacity,
                          }}
                        >
                          {Math.round(c.probability).toFixed(0)}%
                        </Typography>
                      </div>
                    </div>
                  ))}
                </Grid>
              </Grid>
              <Grid container justifyContent="center" alignItems="center">
                <Grid item xs={12} sx={{textAlign: 'left', my: 2}}>
                  <Typography sx={{fontSize: '13px'}}>
                    Results Strength:{' '}
                    <span style={{fontWeight: 500}}>
                      {getResultStrenght(mostRecentSymptomChecker.symptom_conditions[0].probability)}{' '}
                    </span>
                    <IconButton
                      onClick={() => {
                        handleModalResultStrength(mostRecentSymptomChecker);
                      }}
                      style={{
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        padding: 0,
                        color: '#D0D0D0',
                      }}
                    >
                      <FontAwesomeIcon icon={faCircleQuestion} size="xs" style={{color: '#D0D0D0'}} />
                    </IconButton>
                  </Typography>
                </Grid>
              </Grid>
              <ButtonsHelpInfo isMostRecent={true} handleButtonsInfo={handleButtonsInfo} />
            </Fragment>
          ) : (
            <Grid
              container
              columns={13}
              sx={{
                display: 'flex',
                flexGrow: 1,
                justifyContent: 'center',
                overflow: 'auto',
                ...customScrollbarStyles,
              }}
            >
              <Grid
                item
                xs={1}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <IconButton color="primary" component="label" onClick={() => toggleExpanded(0)}>
                  <Avatar
                    sx={{
                      bgcolor: '#c0edee',
                      height: isSmallScreen ? 20 : 28,
                      width: isSmallScreen ? 20 : 28,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <FontAwesomeIcon
                      icon={expandedIndices.includes(0) ? faAngleUp : faAngleDown}
                      size={isSmallScreen ? 'xs' : 'sm'}
                      style={{
                        color: '#63d2d5',
                        paddingTop: !expandedIndices.includes(0) ? '4px' : '0px',
                      }}
                    />
                  </Avatar>
                </IconButton>
              </Grid>
              <Grid item xs={2.7} sx={{textAlign: 'center'}}>
                <Typography>{splittedDate}</Typography>
                <Typography>{mostRecentDay.toUpperCase()}</Typography>
              </Grid>
              <Grid item xs={2}>
                <Typography>{createdTimeMostRecent}</Typography>
              </Grid>
              <Grid item xs={7}>
                {expandedIndices.includes(0) ? (
                  mostRecentSymptomChecker.symptom_conditions.map((c, subIndex) => (
                    <div
                      key={subIndex}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{flex: 1}}>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: '14px',
                          }}
                        >
                          {c.name.replace(/^\s*\w/, match => match.toUpperCase())}
                        </Typography>
                      </div>
                      <div>
                        <Typography
                          key={subIndex}
                          sx={{
                            fontWeight: 700,
                            color: subIndex < 1 ? probColours[subIndex].color : probColours[1].color,
                            opacity: subIndex < 1 ? probColours[subIndex].opacity : probColours[1].opacity,
                          }}
                        >
                          {Math.round(c.probability).toFixed(0)}%
                        </Typography>
                      </div>
                    </div>
                  ))
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{flex: 1}}>
                      <Typography sx={{fontWeight: 500, fontSize: '14px'}}>
                        {mostRecentSymptomChecker.symptom_conditions[0].name.replace(/^\s*\w/, match =>
                          match.toUpperCase(),
                        )}
                      </Typography>
                    </div>
                    <div>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: probColours[0].color,
                          opacity: probColours[0].opacity,
                        }}
                      >
                        {Math.round(mostRecentSymptomChecker.symptom_conditions[0].probability).toFixed(0)}%
                      </Typography>
                    </div>
                  </div>
                )}
              </Grid>
              <Grid item xs={11} sx={{textAlign: 'left', my: 2, ml: 2}}>
                <Typography sx={{fontSize: '15px'}}>
                  Results Strength:{' '}
                  <span style={{fontWeight: 500}}>
                    {getResultStrenght(mostRecentSymptomChecker.symptom_conditions[0].probability)}{' '}
                  </span>
                  <IconButton
                    onClick={() => {
                      handleModalResultStrength(mostRecentSymptomChecker);
                    }}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      padding: 0,
                      color: '#D0D0D0',
                    }}
                  >
                    <FontAwesomeIcon icon={faCircleQuestion} size="xs" style={{color: '#D0D0D0'}} />
                  </IconButton>
                </Typography>
              </Grid>
              <Grid item xs={12.3} sx={{textAlign: 'left', my: 2, ml: 2}}>
                <ButtonsHelpInfo isMostRecent={true} handleButtonsInfo={handleButtonsInfo} />
              </Grid>
            </Grid>
          )
        ) : (
          <Box
            height={200}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <FontAwesomeIcon icon={faTriangleExclamation} style={{fontSize: '50px', color: 'grey'}} />
            <Typography color="grey" textAlign="center" mt={3}>
              There is no recent symptom checker records found.
            </Typography>
          </Box>
        )}
      </TabPanel>
      {/* Content for Past tab */}
      <TabPanel value={activeTab} index={1} sx={{height: '85%', overflowY: 'scroll', ...customScrollbarStyles}}>
        {pastSymptomCheckerHistory.length > 0 ? (
          <div
            style={{
              height: '100%',
              overflowY: 'scroll',
              margin: '0 auto',
              maxWidth: '800px',
            }}
          >
            {pastSymptomCheckerHistory.map((condition, index) => {
              const dateObject = new Date(condition.created_time);

              const monthDayYear = dateObject.toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'short',
                year: isSmallScreen ? '2-digit' : 'numeric',
              });
              const createdDay = dateObject.toLocaleDateString(undefined, {
                weekday: isSmallScreen ? 'short' : 'long',
              });

              const createdTime = dateObject.toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true,
              });

              return isSmallScreen ? (
                <Fragment key={index}>
                  <Grid container columns={15} sx={{margin: '0 auto', maxWidth: '800px'}}>
                    <Grid item xs={4} sx={{mr: 1}}>
                      <Box sx={{textAlign: 'center'}}>
                        <Typography sx={{fontSize: '13px'}}>{monthDayYear}</Typography>
                        <Typography sx={{fontSize: '13px'}}>{createdDay}</Typography>
                        <Typography sx={{fontSize: '13px'}}>{createdTime}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={10} sx={{ml: 1}}>
                      {condition.symptom_conditions.map((c, subIndex) => (
                        <div
                          key={subIndex}
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{flex: 1}}>
                            <Typography sx={{fontWeight: 500, fontSize: '14px'}}>
                              {c.name.replace(/^\s*\w/, match => match.toUpperCase())}
                            </Typography>
                          </div>
                          <div>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: '14px',
                                color: subIndex < 1 ? probColours[subIndex].color : probColours[1].color,
                                opacity: subIndex < 1 ? probColours[subIndex].opacity : probColours[1].opacity,
                              }}
                            >
                              {Math.round(c.probability).toFixed(0)}%
                            </Typography>
                          </div>
                        </div>
                      ))}
                    </Grid>
                  </Grid>
                  <Grid container justifyContent="center" alignItems="center">
                    <Grid item xs={12} sx={{textAlign: 'left', my: 2}}>
                      <Typography sx={{fontSize: '13px'}}>
                        Results Strength:{' '}
                        <span style={{fontWeight: 500}}>
                          {getResultStrenght(condition.symptom_conditions[0].probability)}{' '}
                        </span>
                        <IconButton
                          onClick={() => {
                            handleModalResultStrength(condition);
                          }}
                          style={{
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            color: '#D0D0D0',
                          }}
                        >
                          <FontAwesomeIcon icon={faCircleQuestion} size="xs" style={{color: '#D0D0D0'}} />
                        </IconButton>
                      </Typography>
                    </Grid>
                  </Grid>
                  <ButtonsHelpInfo isMostRecent={false} handleButtonsInfo={handleButtonsInfo} />
                </Fragment>
              ) : (
                <Grid
                  container
                  columns={13}
                  sx={{
                    display: 'flex',
                    flexGrow: 1,
                    justifyContent: 'center',
                    overflow: 'auto',
                    ...customScrollbarStyles,
                  }}
                >
                  <Grid
                    item
                    xs={1}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <IconButton color="primary" component="label" onClick={() => toggleExpanded(index)}>
                      <Avatar
                        sx={{
                          bgcolor: '#c0edee',
                          height: isSmallScreen ? 20 : 28,
                          width: isSmallScreen ? 20 : 28,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FontAwesomeIcon
                          icon={expandedIndices.includes(index) ? faAngleUp : faAngleDown}
                          size={isSmallScreen ? 'xs' : 'sm'}
                          style={{
                            color: '#63d2d5',
                            paddingTop: !expandedIndices.includes(index) ? '4px' : '0px',
                          }}
                        />
                      </Avatar>
                    </IconButton>
                  </Grid>
                  <Grid item xs={2.7} sx={{textAlign: 'center'}}>
                    <Typography>{monthDayYear}</Typography>
                    <Typography>{createdDay.toUpperCase()}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography>{createdTime}</Typography>
                  </Grid>
                  <Grid item xs={7}>
                    {expandedIndices.includes(index) ? (
                      condition.symptom_conditions.map((c, subIndex) => (
                        <div
                          key={subIndex}
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          <div style={{flex: 1}}>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                fontSize: '14px',
                              }}
                            >
                              {c.name.replace(/^\s*\w/, match => match.toUpperCase())}
                            </Typography>
                          </div>
                          <div>
                            <Typography
                              key={subIndex}
                              sx={{
                                fontWeight: 700,
                                color: subIndex < 1 ? probColours[subIndex].color : probColours[1].color,
                                opacity: subIndex < 1 ? probColours[subIndex].opacity : probColours[1].opacity,
                              }}
                            >
                              {Math.round(c.probability).toFixed(0)}%
                            </Typography>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}
                      >
                        <div style={{flex: 1}}>
                          <Typography sx={{fontWeight: 500, fontSize: '14px'}}>
                            {condition.symptom_conditions[0].name.replace(/^\s*\w/, match => match.toUpperCase())}
                          </Typography>
                        </div>
                        <div>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              color: probColours[0].color,
                              opacity: probColours[0].opacity,
                            }}
                          >
                            {Math.round(condition.symptom_conditions[0].probability).toFixed(0)}%
                          </Typography>
                        </div>
                      </div>
                    )}
                  </Grid>
                  <Grid item xs={11} sx={{textAlign: 'left', my: 2, ml: 2}}>
                    <Typography sx={{fontSize: '15px'}}>
                      Results Strength:{' '}
                      <span style={{fontWeight: 500}}>
                        {getResultStrenght(condition.symptom_conditions[0].probability)}{' '}
                      </span>
                      <IconButton
                        onClick={() => {
                          handleModalResultStrength(condition);
                        }}
                        style={{
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          color: '#D0D0D0',
                        }}
                      >
                        <FontAwesomeIcon icon={faCircleQuestion} size="xs" style={{color: '#D0D0D0'}} />
                      </IconButton>
                    </Typography>
                  </Grid>
                  <Grid item xs={12.3} sx={{textAlign: 'left', my: 2, ml: 2}}>
                    <ButtonsHelpInfo isMostRecent={false} handleButtonsInfo={handleButtonsInfo} />
                  </Grid>
                </Grid>
              );
            })}
          </div>
        ) : (
          <Box
            height={200}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <FontAwesomeIcon icon={faTriangleExclamation} style={{fontSize: '50px', color: 'grey'}} />
            <Typography color="grey" textAlign="center" mt={3}>
              There is no past symptom checker records found.
            </Typography>
          </Box>
        )}
      </TabPanel>
      <Dialog
        open={isOpenResultStrength}
        maxWidth="sm"
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
        PaperProps={{
          sx: {
            borderRadius: '30px',
            position: 'absolute',
            mx: {
              xs: 2,
              md: 'auto',
            },
          },
        }}
        sx={{
          top: theme.spacing(5),
          pt: theme.spacing(2),
          pb: theme.spacing(1),
        }}
      >
        <DialogTitle sx={{paddingY: theme.spacing(2)}}>
          <Box sx={{display: 'flex', alignItems: 'flex-start'}}>
            <Typography variant="h6" sx={{fontWeight: 'bold'}}>
              Results Strength
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent
          sx={{
            paddingRight: theme.spacing(3),
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={3}>
              <Typography
                variant="body2"
                style={{
                  textAlign: 'left',
                  color: '#00adb5',
                }}
              >
                {resultsStrength.level.toUpperCase()}
              </Typography>
              {resultsStrength.level.toUpperCase() === 'LOW' && (
                <Typography
                  variant="body2"
                  style={{
                    textAlign: 'left',
                    color: '#00adb5',
                    fontSize: '12px',
                  }}
                >
                  Add more symptoms to improve results.
                </Typography>
              )}
            </Grid>
            <Grid item xs={9}>
              <Typography variant="body2" sx={{mb: 2}}>
                The symptoms you entered are a {resultsStrength.level.toLowerCase()} match with the symptoms of this
                condition.
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="body2" style={{textAlign: 'left'}}>
                Results Strength:{' '}
              </Typography>
            </Grid>
            <Grid item xs={5}>
              <Typography variant="body2" style={{textAlign: 'left'}}>
                <span style={{color: '#00adb5'}}>{resultsStrength.level.toUpperCase()}</span>
              </Typography>
              <HorizontalDashIcon
                totalLines={4}
                linesWithColorA={resultsStrength.linesColourBlue}
                sx={{width: '100%'}}
              />
            </Grid>
          </Grid>
          <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>
            Understanding Your Results
          </Typography>
          <Typography variant="body2">
            • Match strength shows how well the symptoms you entered match the symptoms of each condition. It's not the
            likelihood of having the condition.
          </Typography>
          <Typography variant="body2">
            • Just because a condition is listed here, doesn't mean that you have it. Some of the conditions on this
            list are less frequently diagnosed.
          </Typography>
          <Typography variant="body2">• Talk to your doctor to understand what your symptoms may mean.</Typography>
        </DialogContent>
        <DialogActions sx={{paddingBottom: theme.spacing(2)}}>
          <Button variant="text" onClick={() => closeModalResultStrenght(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function TabPanel(props) {
  const {children, value, index, ...other} = props;

  return (
    <Box role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box p={3}>{children}</Box>}
    </Box>
  );
}

export default SymptomCheckerHistoryTabPanel;
